import React, { useState } from "react";

import { ActiveTab, ActiveTabWithoutAll, StoryQuestion, Suggestion } from "chq";
import { useIsMobile } from "app/hooks";
import { t, Trans } from "app/utils";

import magicWandIcon from "images/magic-wand_gradient.svg";

import { Icon } from "@culturehq/components";
import { makePost } from "@culturehq/client";

import { usePostStoryContext } from "components/stories/post-stories-v2/contexts/PostStoryContext";
import PostStoryMediaButtons from "./PostStoryMediaButtons";
import PostStoryImageButtons from "./PostStoryImageButtons";
import PostStoryMediaAI from "./PostStoryMediaAI";
import MagicScript from "./MagicScript";
import PromptMediaTypeTab from "./PromptMediaTypeTab";

type PromptAddMediaProps = {
  prompt: StoryQuestion;
  suggestion: Suggestion;
  chatId: string;
  storyQuestionId: number;
  onSuggestionChange: (suggestion: Partial<Suggestion>) => void;
  onLoading: (value: boolean) => void;
  onNextStep: () => void;
};

const PromptAddMedia: React.FC<PromptAddMediaProps> = ({
  prompt,
  suggestion,
  chatId,
  storyQuestionId,
  onSuggestionChange,
  onLoading,
  onNextStep
}) => {
  const isMobile = useIsMobile();
  const justImages = "just_images";
  const justVideos = "just_videos";
  const isAllowedTypesAll = prompt.allowedTypes === "all";
  const { state: { selectedImages, selectedMedia }, restartSelectedMedia } = usePostStoryContext();
  const allowedType = (() => {
    const selectedMediaType = (selectedMedia as File)?.type;
    const allowedType = isAllowedTypesAll
      ? justImages
      : prompt.allowedTypes as ActiveTabWithoutAll;

    if (!selectedMediaType) return allowedType;

    return selectedMediaType.includes("image") ? justImages : justVideos;
  })();
  const [activeTab, setActiveTab] = useState<ActiveTabWithoutAll>(allowedType);
  const [magicScriptText, setMagicScriptText] = useState(suggestion?.media?.video);
  const activeTabIsJustVideos = activeTab === justVideos;

  const handleChangeActiveTab = (tab: ActiveTabWithoutAll) => {
    setActiveTab(tab);
    restartSelectedMedia();
  };

  const handleMagicScriptChange = (value: string) => {
    setMagicScriptText(value);
  };

  const handleOnClickRefresh = () => {
    onLoading(true);
    makePost("/ai/public/reload_media_suggestion", { chatId, storyQuestionId })
      .then(({ suggestion }: any) => {
        if (activeTabIsJustVideos) {
          setMagicScriptText(suggestion.media.video);
          // eslint-disable-next-line no-param-reassign
          delete suggestion.media.images;
        } else {
          // eslint-disable-next-line no-param-reassign
          delete suggestion.media.video;
        }

        onSuggestionChange({ media: suggestion.media });
        onLoading(false);
      });
  };

  return (
    <>
      <div className="MainContainerIntelligence__Edit">
        {!isMobile && (
          <div className="MainContainerIntelligence__Title-media">
            <h2><Trans>Add Media</Trans></h2>
          </div>
        )}
        {isAllowedTypesAll && (
          <div className="MainContainerIntelligence__Toggle-container">
            <PromptMediaTypeTab
              activeTab={activeTab}
              label="Photo"
              onChangeActiveTab={handleChangeActiveTab}
              hasSelectedMedia={
                selectedImages.length > 0 || (!!selectedMedia && !(selectedMedia as any).thumbUrl)
              }
              tab={justImages}
            />
            <PromptMediaTypeTab
              activeTab={activeTab}
              label="Video"
              onChangeActiveTab={handleChangeActiveTab}
              hasSelectedMedia={
                selectedImages.length > 0 || (!!selectedMedia && !(selectedMedia as any).thumbUrl)
              }
              tab={justVideos}
            />
          </div>
        )}
        {!isMobile && (
          <>
            {activeTabIsJustVideos ? (
              <PostStoryMediaButtons
                magicScriptText={magicScriptText}
                onNextStep={onNextStep}
              />
            ) : (
              <PostStoryImageButtons onNextStep={onNextStep} />
            )}
          </>
        )}
        <div className="MainContainerIntelligence__Scrollable no-scrollbar">
          {(selectedImages.length > 0 || !!selectedMedia) && !isMobile && (
            <div className="SelectedMedia-ai">
              <PostStoryMediaAI />
            </div>
          )}

          <div className="MainContainerIntelligence__Title-script">
            <img style={{ width: "24px", height: "24px" }} src={magicWandIcon} alt="Magic Wand" />
            <h4>{activeTabIsJustVideos ? t("Magic Script") : t("Magic Photo Recommendations")}</h4>
            <Icon className="refresh-icon" icon="refresh" onClick={handleOnClickRefresh} />
          </div>
          <div className="MainContainerIntelligence__Suggestions no-scrollbar">
            {activeTabIsJustVideos ? (
              <MagicScript
                hasSelectedVideo={!!selectedMedia && !(selectedMedia as any).thumbUrl}
                magicScriptText={magicScriptText}
                onMagicScriptChange={handleMagicScriptChange}
              />
            ) : (
              <>
                {suggestion?.media?.images?.map((image, index) => (
                  <div className="MainContainerIntelligence__Textarea-suggestion no-scrollbar" key={index}>
                    <h3>{image.title}</h3>
                    <p className="caption">{image.content}</p>
                  </div>
                ))}
              </>
            )}
            <div className="Scrollable-Overlay" />
          </div>
        </div>
      </div>
      {isMobile && (
        <div className="MobileButtons col-xs-12">
          {activeTabIsJustVideos ? (
            <PostStoryMediaButtons
              magicScriptText={magicScriptText}
              onNextStep={onNextStep}
            />
          ) : (
            <PostStoryImageButtons onNextStep={onNextStep} />
          )}
        </div>
      )}
    </>
  );
};

export default PromptAddMedia;
